import { navigationEpoch } from '../../navigation/loadEpoch';
import { getPersistenceNamespace } from '../helpers/persistenceHelpers';
import type { StateCreator } from 'zustand';
import type { VoyagerState } from '../useVoyagerStore';
import type {
  Dashboard,
  DatasetData,
  CreateDatasetBody,
  DashboardFilter,
  RecommendationsData,
  LayoutPanel,
  RglLayoutItem,
  GridPos,
} from '../../dashboards/dashboardTypes';
import type { ChartSpec } from '../../dashboards/chartSpec';
import * as api from '../../apis';

export interface DashboardsSlice {
  dashboards: Dashboard[];
  currentDashboard: Dashboard | null;
  dashboardsLoading: boolean;
  dashboardError: string | null;
  datasetData: Record<string, DatasetData>;       // keyed by dataset id
  datasetDataLoading: Record<string, boolean>;

  fetchDashboards: () => Promise<void>;
  createNewDashboard: (name: string) => Promise<string | null>;
  openDashboardById: (id: string, signal?: AbortSignal) => Promise<void>;
  closeDashboard: () => void;
  renameDashboard: (name: string) => Promise<void>;
  deleteCurrentDashboard: () => Promise<void>;

  addDataset: (body: CreateDatasetBody) => Promise<boolean>;
  removeDataset: (datasetId: string) => Promise<void>;
  loadDatasetData: (datasetId: string, force?: boolean) => Promise<void>;

  recommendForDataset: (datasetId: string, query?: string) => Promise<{ recommendations: ChartSpec[]; error: string | null; curated: boolean }>;
  loadRecommendations: (datasetId: string) => Promise<{ data: RecommendationsData | null; error: string | null }>;
  applyLayout: (datasetId: string, panels: LayoutPanel[]) => Promise<void>;
  addVisualization: (datasetId: string, chartSpec: ChartSpec) => Promise<void>;
  updateVizSpec: (vizId: string, chartSpec: ChartSpec) => Promise<void>;
  removeVisualization: (vizId: string) => Promise<void>;
  persistLayout: (layout: RglLayoutItem[]) => Promise<void>;
  setDashboardFilters: (filters: DashboardFilter[]) => Promise<void>;
}

const DEFAULT_TILE: GridPos = { x: 0, y: Infinity as any, w: 6, h: 9 };

function errMsg(e: any): string {
  return e?.response?.data?.error || e?.message || 'Request failed';
}

export const createDashboardsSlice: StateCreator<
  VoyagerState, [], [], DashboardsSlice
> = (set, get) => ({
  dashboards: [],
  currentDashboard: null,
  dashboardsLoading: false,
  dashboardError: null,
  datasetData: {},
  datasetDataLoading: {},

  fetchDashboards: async () => {
    const namespace = getPersistenceNamespace(), loadEpoch = navigationEpoch();
    set({ dashboardsLoading: true, dashboardError: null });
    try {
      const dashboards = await api.listDashboards();
      if (namespace !== getPersistenceNamespace() || loadEpoch !== navigationEpoch()) return;
      set({ dashboards, dashboardsLoading: false });
    } catch (e) {
      if (namespace !== getPersistenceNamespace() || loadEpoch !== navigationEpoch()) return;
      set({ dashboardError: errMsg(e), dashboardsLoading: false });
    }
  },

  createNewDashboard: async (name: string) => {
    try {
      const d = await api.createDashboard({ name });
      set({ dashboards: [d, ...get().dashboards] });
      return d.id;
    } catch (e) {
      set({ dashboardError: errMsg(e) });
      return null;
    }
  },

  openDashboardById: async (id: string, signal?: AbortSignal) => {
    const namespace = getPersistenceNamespace();
    const loadEpoch = navigationEpoch();
    set({ dashboardsLoading: true, dashboardError: null, datasetData: {} });
    try {
      const dashboard = await api.getDashboard(id);
      if (loadEpoch !== navigationEpoch() || signal?.aborted || namespace !== getPersistenceNamespace()) return;
      set({ currentDashboard: dashboard, dashboardsLoading: false });
    } catch (e) {
      if (loadEpoch !== navigationEpoch() || signal?.aborted || namespace !== getPersistenceNamespace()) return;
      set({ dashboardError: errMsg(e), dashboardsLoading: false });
    }
  },

  closeDashboard: () => set({ currentDashboard: null, datasetData: {} }),

  renameDashboard: async (name: string) => {
    const cur = get().currentDashboard;
    if (!cur) return;
    try {
      const updated = await api.updateDashboard(cur.id, { name });
      set({
        currentDashboard: { ...cur, name: updated.name },
        dashboards: get().dashboards.map((d) => (d.id === cur.id ? { ...d, name: updated.name } : d)),
      });
    } catch (e) {
      set({ dashboardError: errMsg(e) });
    }
  },

  deleteCurrentDashboard: async () => {
    const cur = get().currentDashboard;
    if (!cur) return;
    try {
      await api.deleteDashboard(cur.id);
      set({
        currentDashboard: null,
        dashboards: get().dashboards.filter((d) => d.id !== cur.id),
      });
    } catch (e) {
      set({ dashboardError: errMsg(e) });
    }
  },

  addDataset: async (body: CreateDatasetBody) => {
    const cur = get().currentDashboard;
    if (!cur) return false;
    try {
      const dataset = await api.createDataset(cur.id, body);
      set({
        currentDashboard: { ...cur, datasets: [...(cur.datasets || []), dataset] },
      });
      return true;
    } catch (e) {
      set({ dashboardError: errMsg(e) });
      return false;
    }
  },

  removeDataset: async (datasetId: string) => {
    const cur = get().currentDashboard;
    if (!cur) return;
    try {
      await api.deleteDataset(cur.id, datasetId);
      set({
        currentDashboard: {
          ...cur,
          datasets: (cur.datasets || []).filter((d) => d.id !== datasetId),
          // visualizations on this dataset are cascade-deleted server-side
          visualizations: (cur.visualizations || []).filter((v) => v.dataset_id !== datasetId),
        },
      });
    } catch (e) {
      set({ dashboardError: errMsg(e) });
    }
  },

  loadDatasetData: async (datasetId: string, force = false) => {
    const cur = get().currentDashboard;
    if (!cur) return;
    if (!force && get().datasetData[datasetId]) return;
    set({ datasetDataLoading: { ...get().datasetDataLoading, [datasetId]: true } });
    try {
      const data = await api.getDatasetData(cur.id, datasetId, cur.filters);
      set({
        datasetData: { ...get().datasetData, [datasetId]: data },
        datasetDataLoading: { ...get().datasetDataLoading, [datasetId]: false },
      });
    } catch (e) {
      set({
        dashboardError: errMsg(e),
        datasetDataLoading: { ...get().datasetDataLoading, [datasetId]: false },
      });
    }
  },

  recommendForDataset: async (datasetId: string, query?: string) => {
    const cur = get().currentDashboard;
    if (!cur) return { recommendations: [], error: null, curated: false };
    try {
      const { recommendations, curated } = await api.recommendVisualizations(cur.id, datasetId, query, cur.filters);
      return { recommendations, error: null, curated: !!curated };
    } catch (e) {
      // Surface the real failure to the caller instead of a silent empty list.
      return { recommendations: [], error: errMsg(e), curated: false };
    }
  },

  loadRecommendations: async (datasetId: string) => {
    const cur = get().currentDashboard;
    if (!cur) return { data: null, error: null };
    try {
      const data = await api.getRecommendations(cur.id, datasetId, cur.filters);
      return { data, error: null };
    } catch (e) {
      return { data: null, error: errMsg(e) };
    }
  },

  applyLayout: async (datasetId: string, panels: LayoutPanel[]) => {
    const cur = get().currentDashboard;
    if (!cur) return;
    const created: { viz: any; pos: GridPos }[] = [];
    try {
      for (const panel of panels) {
        // eslint-disable-next-line no-await-in-loop
        const viz = await api.createVisualization(cur.id, {
          dataset_id: datasetId, chart_spec: panel.chart_spec, position: panel.position,
        });
        created.push({ viz, pos: panel.position });
      }
    } catch (e) {
      set({ dashboardError: errMsg(e) });
    }
    const existing = Array.isArray(cur.layout) ? cur.layout : [];
    const newLayout: RglLayoutItem[] = [...existing, ...created.map((c) => ({ i: c.viz.id, ...c.pos }))];
    set({
      currentDashboard: {
        ...cur,
        visualizations: [...(cur.visualizations || []), ...created.map((c) => c.viz)],
        layout: newLayout,
      },
    });
    try {
      await api.updateDashboard(cur.id, { layout: newLayout });
    } catch (e) {
      set({ dashboardError: errMsg(e) });
    }
  },

  addVisualization: async (datasetId: string, chartSpec: ChartSpec) => {
    const cur = get().currentDashboard;
    if (!cur) return;
    try {
      const viz = await api.createVisualization(cur.id, {
        dataset_id: datasetId,
        chart_spec: chartSpec,
        position: DEFAULT_TILE,
      });
      set({ currentDashboard: { ...cur, visualizations: [...(cur.visualizations || []), viz] } });
    } catch (e) {
      set({ dashboardError: errMsg(e) });
    }
  },

  updateVizSpec: async (vizId: string, chartSpec: ChartSpec) => {
    const cur = get().currentDashboard;
    if (!cur) return;
    try {
      const updated = await api.updateVisualization(cur.id, vizId, { chart_spec: chartSpec });
      set({
        currentDashboard: {
          ...cur,
          visualizations: (cur.visualizations || []).map((v) => (v.id === vizId ? updated : v)),
        },
      });
    } catch (e) {
      set({ dashboardError: errMsg(e) });
    }
  },

  removeVisualization: async (vizId: string) => {
    const cur = get().currentDashboard;
    if (!cur) return;
    try {
      await api.deleteVisualization(cur.id, vizId);
      set({
        currentDashboard: {
          ...cur,
          visualizations: (cur.visualizations || []).filter((v) => v.id !== vizId),
        },
      });
    } catch (e) {
      set({ dashboardError: errMsg(e) });
    }
  },

  persistLayout: async (layout: RglLayoutItem[]) => {
    const cur = get().currentDashboard;
    if (!cur) return;
    set({ currentDashboard: { ...cur, layout } });
    try {
      await api.updateDashboard(cur.id, { layout });
    } catch (e) {
      set({ dashboardError: errMsg(e) });
    }
  },

  setDashboardFilters: async (filters: DashboardFilter[]) => {
    const cur = get().currentDashboard;
    if (!cur) return;
    // apply immediately, clear cached data, persist, then reload every dataset
    set({ currentDashboard: { ...cur, filters }, datasetData: {} });
    try {
      await api.updateDashboard(cur.id, { filters });
    } catch (e) {
      set({ dashboardError: errMsg(e) });
    }
    for (const d of cur.datasets || []) {
      // eslint-disable-next-line no-await-in-loop
      await get().loadDatasetData(d.id, true);
    }
  },
});
